import { useEffect, lazy, Suspense, useState } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AnimatePresence } from "framer-motion";
import { AuthProvider, useAuth } from "@/hooks/useAuth";
import { useOffline } from "@/hooks/useOffline";
import ProtectedRoute from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { SensoryProvider } from "@/contexts/SensoryContext";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import Login from "./pages/Login";
import Onboarding from "./pages/Onboarding";
import Install from "./pages/Install";
import NotFound from "./pages/NotFound";
import Home from "./pages/Home";
import { useCaptureStore } from "@/stores/captureStore";
import { setupAuthRefresh } from "@/lib/authServiceWorker";
import { supabase } from "@/integrations/supabase/client";
import { migrateLegacyOnboarding } from "@/lib/migrateLegacyOnboarding";

// Lazy load FAB e Modal - usando React.lazy padrão (mais confiável)
const CaptureFAB = lazy(() => import("@/components/capture/CaptureFAB").then(m => ({ default: m.CaptureFAB })));
const CaptureModal = lazy(() => import("@/components/capture/CaptureModal").then(m => ({ default: m.CaptureModal })));

// Code Splitting - Lazy load páginas autenticadas
const Today = lazy(() => import("./pages/Today"));
const Processamento = lazy(() => import("./pages/Processamento"));
const ProcessamentoZen = lazy(() => import("./pages/ProcessamentoZen"));
const Journeys = lazy(() => import("./pages/Journeys"));
const Profile = lazy(() => import("./pages/Profile"));
const Focus = lazy(() => import("./pages/Focus"));
const TaskSubtasks = lazy(() => import("./pages/TaskSubtasks"));
const AllTasks = lazy(() => import("./pages/AllTasks"));
const Debug = lazy(() => import("./pages/Debug"));
const Insights = lazy(() => import("./pages/Insights"));

// ✅ FASE 3: QueryClient será criado dentro do componente App com useState
// (removido daqui para evitar problemas de timing com React)

// Loading fallback component with timeout (8s) e countdown visual
const PageLoader = () => {
  const [showError, setShowError] = useState(false);
  const [countdown, setCountdown] = useState(8);
  
  useEffect(() => {
    // Countdown visual
    const countInterval = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(countInterval);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    
    // Timeout de 8 segundos (mais agressivo)
    const timer = setTimeout(() => {
      console.error('[PageLoader] Timeout: página não carregou em 8s');
      setShowError(true);
    }, 8000);
    
    return () => {
      clearTimeout(timer);
      clearInterval(countInterval);
    };
  }, []);
  
  if (showError) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <div className="text-center space-y-4 max-w-md">
          <AlertCircle className="w-16 h-16 text-warning mx-auto" />
          <h2 className="text-2xl font-semibold">Carregamento Lento</h2>
          <p className="text-muted-foreground">
            A página está demorando muito para carregar. Isso pode ser um problema de conexão ou cache corrompido.
          </p>
          <div className="flex flex-col gap-2">
            <Button onClick={() => window.location.reload()} className="w-full">
              🔄 Tentar Novamente
            </Button>
            <Button 
              variant="outline" 
              onClick={() => {
                localStorage.clear();
                sessionStorage.clear();
                if ('caches' in window) {
                  caches.keys().then(names => names.forEach(name => caches.delete(name)));
                }
                window.location.href = '/debug';
              }}
              className="w-full"
            >
              🔧 Ir para Diagnóstico
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
        <p className="text-sm text-muted-foreground animate-pulse">
          Carregando... {countdown > 0 && `(${countdown}s)`}
        </p>
      </div>
    </div>
  );
};

// Error component for lazy loading failures
const LazyLoadError = () => (
  <div className="min-h-screen flex items-center justify-center bg-background p-4">
    <div className="text-center space-y-4">
      <AlertCircle className="w-12 h-12 text-destructive mx-auto" />
      <h2 className="text-xl font-semibold">Erro ao carregar página</h2>
      <p className="text-muted-foreground">
        Verifique sua conexão e tente novamente.
      </p>
      <Button onClick={() => window.location.reload()}>
        Recarregar
      </Button>
    </div>
  </div>
);

// Global error component for critical initialization errors
const GlobalError = ({ error }: { error?: Error }) => {
  // Detectar se é erro de Supabase
  const isSupabaseError = error?.message?.includes('Supabase') || 
                         error?.message?.includes('supabase') ||
                         !import.meta.env.VITE_SUPABASE_URL;
  
  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: '#141414',
      color: '#f2f2f2',
      padding: '1rem',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    }}>
      <div style={{ textAlign: 'center', maxWidth: '500px', width: '100%' }}>
        <AlertCircle 
          style={{ 
            width: '64px', 
            height: '64px', 
            color: '#ef4444', 
            margin: '0 auto 24px' 
          }} 
        />
        <div style={{ marginBottom: '24px' }}>
          <h2 style={{ fontSize: '24px', fontWeight: '600', marginBottom: '12px' }}>
            {isSupabaseError ? 'Erro de Configuração' : 'Erro ao carregar a aplicação'}
          </h2>
          <p style={{ color: '#a0a0a0', marginBottom: '16px' }}>
            {isSupabaseError 
              ? 'As variáveis de ambiente não estão configuradas corretamente.'
              : 'Algo deu errado durante a inicialização.'
            }
          </p>
          {error && (
            <details style={{
              backgroundColor: '#1f1f1f',
              padding: '16px',
              borderRadius: '8px',
              textAlign: 'left',
              marginTop: '16px'
            }}>
              <summary style={{ 
                cursor: 'pointer', 
                fontWeight: '600', 
                marginBottom: '12px',
                color: '#f2f2f2'
              }}>
                Detalhes técnicos
              </summary>
              <pre style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                fontSize: '12px',
                color: '#888',
                overflow: 'auto',
                maxHeight: '300px'
              }}>
{error.message}
{'\n\n'}
ENV Check:
VITE_SUPABASE_URL: {import.meta.env.VITE_SUPABASE_URL ? '✅ presente' : '❌ ausente'}
VITE_SUPABASE_PUBLISHABLE_KEY: {import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY ? '✅ presente' : '❌ ausente'}
{'\n\n'}
{error.stack}
              </pre>
            </details>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button 
            onClick={() => window.location.reload()}
            style={{
              backgroundColor: '#3b82f6',
              color: 'white',
              padding: '12px 24px',
              border: 'none',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            Recarregar Página
          </button>
          <button 
            onClick={() => {
              localStorage.clear();
              sessionStorage.clear();
              if ('caches' in window) {
                caches.keys().then(names => {
                  names.forEach(name => caches.delete(name));
                });
              }
              window.location.href = '/';
            }}
            style={{
              backgroundColor: 'transparent',
              color: '#a0a0a0',
              padding: '12px 24px',
              border: '1px solid #333',
              borderRadius: '8px',
              fontSize: '16px',
              fontWeight: '500',
              cursor: 'pointer',
              width: '100%'
            }}
          >
            Limpar Cache e Reiniciar
          </button>
        </div>
      </div>
    </div>
  );
};

// Root redirect component
const RootRedirect = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  
  // Detectar se está em modo PWA
  const isPWA = window.matchMedia('(display-mode: standalone)').matches ||
                (window.navigator as any).standalone === true;
  
  console.log('[RootRedirect]', { 
    user: !!user, 
    loading, 
    isPWA,
    pathname: location.pathname,
    href: window.location.href,
    timestamp: new Date().toISOString()
  });
  
  if (loading) {
    console.log('[RootRedirect] Carregando auth...');
    return <PageLoader />;
  }
  
  // Se está autenticado → /today
  if (user) {
    console.log('[RootRedirect] Autenticado → /today');
    return <Navigate to="/today" replace />;
  }
  
  // Se não está autenticado:
  // - PWA ou acesso direto → /login
  // - Web normal (localhost) → /home (landing page)
  if (isPWA || window.location.hostname !== 'localhost') {
    console.log('[RootRedirect] Não autenticado (PWA/Produção) → /login');
    return <Navigate to="/login" replace />;
  }
  
  console.log('[RootRedirect] Não autenticado (Dev) → /home');
  return <Navigate to="/home" replace />;
};

const AppContent = () => {
  useOffline();
  const { user, loading } = useAuth();
  const { openModal } = useCaptureStore();
  const location = useLocation();
  
  // Definir rotas protegidas onde o FAB deve aparecer
  const protectedRoutes = ['/today', '/processamento', '/journeys', '/profile', '/focus', '/task'];
  const isProtectedRoute = protectedRoutes.some(route => location.pathname.startsWith(route));
  
  // FAB não aparece em modo zen
  const isZenMode = location.pathname === '/processamento-zen';
  
  // FAB só aparece em rotas protegidas com usuário autenticado (mas não em modo zen)
  const shouldShowFAB = !loading && user && isProtectedRoute && !isZenMode;
  
  useEffect(() => {
    // Medir performance de carregamento inicial
    if ('performance' in window) {
      const perfData = performance.getEntriesByType('navigation')[0] as PerformanceNavigationTiming;
      if (perfData) {
        console.log('[Performance] App Load:', {
          dns: Math.round(perfData.domainLookupEnd - perfData.domainLookupStart),
          ttfb: Math.round(perfData.responseStart - perfData.requestStart),
          domReady: Math.round(perfData.domContentLoadedEventEnd - perfData.domContentLoadedEventStart),
        });
      }
    }
  }, []);

  // Atalho global de teclado para captura rápida
  useEffect(() => {
    if (!user) return;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      // Ctrl+K (Windows/Linux) ou Cmd+K (Mac)
      if ((e.ctrlKey || e.metaKey) && e.key === 'k') {
        e.preventDefault();
        openModal();
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [user, openModal]);
  
  return (
    <>
      {/* FAB + Modal - apenas em rotas do painel autenticado */}
      {shouldShowFAB && (
        <Suspense fallback={null}>
          <CaptureFAB />
          <CaptureModal />
        </Suspense>
      )}
      
      <ErrorBoundary fallback={<LazyLoadError />}>
        <Suspense fallback={<PageLoader />}>
          <Routes>
        <Route path="/" element={<RootRedirect />} />
        <Route path="/home" element={<Home />} />
        <Route path="/login" element={<Login />} />
        <Route path="/onboarding" element={
          <ProtectedRoute>
            <Onboarding />
          </ProtectedRoute>
        } />
        <Route path="/today" element={
          <ProtectedRoute>
            <Today />
          </ProtectedRoute>
        } />
        <Route path="/processamento" element={
          <ProtectedRoute>
            <Processamento />
          </ProtectedRoute>
        } />
        <Route path="/processamento-zen" element={
          <ProtectedRoute>
            <ProcessamentoZen />
          </ProtectedRoute>
        } />
        <Route path="/journeys" element={
          <ProtectedRoute>
            <Journeys />
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <Profile />
          </ProtectedRoute>
        } />
        <Route path="/focus" element={
          <ProtectedRoute>
            <Focus />
          </ProtectedRoute>
        } />
        <Route path="/task/:taskId/subtasks" element={
          <ProtectedRoute>
            <TaskSubtasks />
          </ProtectedRoute>
        } />
        <Route path="/todas-tarefas" element={
          <ProtectedRoute>
            <AllTasks />
          </ProtectedRoute>
        } />
        <Route path="/insights" element={
          <ProtectedRoute>
            <Insights />
          </ProtectedRoute>
        } />
        <Route path="/install" element={<Install />} />
        <Route path="/debug" element={<Debug />} />
        <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
      </ErrorBoundary>
    </>
  );
};

const App = () => {
  // ✅ FASE 3: Criar QueryClient com useState para garantir lazy initialization
  // Isso garante que React está COMPLETAMENTE carregado antes de criar o QueryClient
  const [queryClient] = useState(() => new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        gcTime: 10 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          if (failureCount > 2) return false;
          if (error instanceof Error && error.message.includes('network')) {
            return failureCount < 3;
          }
          return false;
        },
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
      },
    },
  }));
  
  useEffect(() => {
    // Log de inicialização
    console.log('[App] 🚀 Iniciando aplicação...', {
      mode: import.meta.env.MODE,
      hasSupabaseUrl: !!import.meta.env.VITE_SUPABASE_URL,
      hasSupabaseKey: !!import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY,
      isPWA: window.matchMedia('(display-mode: standalone)').matches,
      timestamp: new Date().toISOString()
    });
    
    // Migrar localStorage legado de onboarding para banco de dados
    if (!localStorage.getItem('vyra-onboarding-migrated')) {
      migrateLegacyOnboarding();
    }
    
    // ✅ FORÇA ATUALIZAÇÃO DE SERVICE WORKERS
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        console.log('[App] 🔍 Service Workers encontrados:', registrations.length);
        
        registrations.forEach((registration, index) => {
          console.log(`[App] SW ${index + 1}:`, {
            scope: registration.scope,
            waiting: !!registration.waiting,
            active: !!registration.active,
            installing: !!registration.installing
          });
          
          // Forçar update em cada SW
          registration.update().then(() => {
            console.log(`[App] ✅ SW ${index + 1} atualizado`);
          }).catch(err => {
            console.warn(`[App] ⚠️ Erro ao atualizar SW ${index + 1}:`, err);
          });
        });
      }).catch(err => {
        console.warn('[App] ⚠️ Erro ao listar SWs:', err);
      });
      
      // Listener para novo SW instalado
      navigator.serviceWorker.ready.then(registration => {
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          console.log('[App] 🆕 Novo Service Worker detectado!');
          
          if (newWorker) {
            newWorker.addEventListener('statechange', () => {
              if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
                console.log('[App] 🔄 Nova versão disponível! Recarregando...');
                // Aguardar 1s para garantir que o SW novo esteja pronto
                setTimeout(() => {
                  window.location.reload();
                }, 1000);
              }
            });
          }
        });
      }).catch(err => {
        console.warn('[App] ⚠️ Erro ao registrar listener de update:', err);
      });
    }
    
    // Configurar refresh automático de autenticação
    const cleanup = setupAuthRefresh(supabase);
    return cleanup;
  }, []);

  return (
    <ErrorBoundary fallback={<GlobalError />}>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <AuthProvider>
              <SensoryProvider>
                <AppContent />
              </SensoryProvider>
            </AuthProvider>
          </BrowserRouter>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

export default App;
